import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { ApiError, api } from "./client";
import type { StudentTrainingCohort } from "./trainingCohorts";

export type DirectoryStudent = {
  id: string;
  full_name: string;
  phone: string | null;
  branch_id: string;
  training_subgroup_id?: string | null;
  training_cohort?: StudentTrainingCohort | null;
};

export type Instructor = {
  id: string;
  full_name: string;
  employment_type: "full_time" | "daily_paid" | string;
  certificate_expiry_date: string | null;
  branch_id: string;
};

export type Vehicle = {
  id: string;
  plate_no: string;
  brand_model: string | null;
  gear_type?: string | null;
  current_km: number;
  branch_id: string;
};

export type DirectoryBundle = {
  students: DirectoryStudent[];
  instructors: Instructor[];
  vehicles: Vehicle[];
};

export type DirectoryCohortFilter = {
  trainingPeriodId?: string;
  trainingGroupId?: string;
  trainingSubgroupId?: string;
};

export type DirectoryState = {
  data: DirectoryBundle | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

let cachedBundle: DirectoryBundle | null = null;
let cachedKey = "";
let cachedAt = 0;
const CACHE_TTL_MS = 60 * 1000;

function buildCacheKey(
  sessionKey: string,
  branchId?: string,
  cohort?: DirectoryCohortFilter,
): string {
  return [
    sessionKey,
    branchId || "",
    cohort?.trainingPeriodId || "",
    cohort?.trainingGroupId || "",
    cohort?.trainingSubgroupId || "",
  ].join("|");
}

function buildCohortQuery(cohort?: DirectoryCohortFilter): Record<string, string | undefined> {
  if (!cohort) return {};
  return {
    training_period_id: cohort.trainingPeriodId || undefined,
    training_group_id: cohort.trainingGroupId || undefined,
    training_subgroup_id: cohort.trainingSubgroupId || undefined,
  };
}

export function useDirectoryBundle(
  branchId?: string,
  cohortFilter?: DirectoryCohortFilter,
): DirectoryState {
  const { session } = useAuth();
  const effectiveBranchId =
    branchId ??
    (session?.user?.is_platform_owner ? undefined : session?.user?.branch_id ?? undefined);
  const sessionKey = session
    ? `${session.user.tenant_id}:${session.user.id}:${session.user.role}:${session.user.branch_id ?? ""}`
    : "anon";
  const cacheKey = buildCacheKey(sessionKey, effectiveBranchId, cohortFilter);
  const [data, setData] = useState<DirectoryBundle | null>(
    cachedKey === cacheKey ? cachedBundle : null,
  );
  const [loading, setLoading] = useState<boolean>(cachedKey !== cacheKey || !cachedBundle);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const fresh = cachedKey === cacheKey && cachedBundle && Date.now() - cachedAt < CACHE_TTL_MS;
    if (fresh && reloadKey === 0) {
      setData(cachedBundle);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<DirectoryBundle>(
        "/directory/bundle",
        { branch_id: effectiveBranchId, ...buildCohortQuery(cohortFilter) },
        { signal: controller.signal },
      )
      .then((response) => {
        cachedBundle = response;
        cachedKey = cacheKey;
        cachedAt = Date.now();
        setData(response);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError) setError(err.message);
        else setError("Liste verileri yuklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [sessionKey, effectiveBranchId, cacheKey, cohortFilter?.trainingPeriodId, cohortFilter?.trainingGroupId, cohortFilter?.trainingSubgroupId, reloadKey]);

  return {
    data,
    loading,
    error,
    reload: () => setReloadKey((k) => k + 1),
  };
}

export function clearDirectoryCache() {
  cachedBundle = null;
  cachedKey = "";
  cachedAt = 0;
}
